import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Auth from '../../../../lib/commons/auth'
import {
  fetchMe,
  updateMeDelta,
  UserDetailDto,
} from '../../../../infrastructure/api/users'
import { fromDetail, MeFormModel, toDeltaInput } from '../model'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { extractValuesFromResponse } from '../../../../lib/commons/api'
import { handleWarning } from '../../../../handlers/globalErrorHandler'
import { APPLICATION_FUNCTION_EXTERNAL_ID } from '../..'
import { useMeFormState } from './useMeFormState'

// ユーザー情報画面の data フック。更新のみ（作成・削除は無い）。
// NOTE: APPLICATION_FUNCTION_EXTERNAL_ID はモジュールトップレベルで読むと
// pages/index.tsx との循環参照で初期化前アクセス（undefined）になるため、
// 必ず実行時（コールバック内）で参照する。
export const useMeEditData = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [entity, setEntity] = useState<UserDetailDto | undefined>()
  // isSubmitting は再描画まで反映されないため、その間に連続で保存されると同じ delta が
  // 二重に送信されうる。Slack連携の追加は二重送信されると一意制約違反でエラーになる。
  const isSubmittingRef = useRef(false)
  // isLoading は保存中に挟む再取得が終わった時点で false に戻るため、保存の完了より
  // 先にボタンが有効化されてしまう。押しても isSubmittingRef に弾かれて無反応になる
  // ため、保存の間ずっと無効化できるよう送信中かどうかを別に持つ。
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Auth 保持のユーザー情報は保存後の再取得に成功してから更新する。再取得に失敗した
  // 場合も画面内のリロードで取り直した時点で更新できるよう、未反映かどうかを覚える。
  const shouldRefreshUserRef = useRef(false)
  const {
    formModel,
    setFormModel,
    formModelRef,
    onChangeMe,
    edited,
    refreshedPageData,
    editedFormData,
    refreshEditedFormData,
    markEditsSince,
  } = useMeFormState()

  // 保存直後の再取得では、保存中に入力された内容を残す判定の基準にするため、保存
  // した内容を submittedFormModel として渡す。
  const fetchData = useCallback(
    async (submittedFormModel?: MeFormModel) => {
      setIsLoading(true)
      try {
        const detail = await fetchMe()
        if (!detail) return undefined
        // 取得に失敗した場合に空のフォームモデルを実データとして描画したり、
        // 画面に残っている編集内容を保存できなくしたりしないよう、画面の表示と
        // 編集フラグのリセットは取得に成功した場合のみ行う。
        setEntity(detail)
        const refetchedFormModel = fromDetail(detail)
        if (submittedFormModel) {
          // 保存を始めてから再取得が終わるまでに入力された内容は今回の保存対象に
          // 含まれていないため、再取得結果で上書きすると入力が黙って失われる。
          // 編集済みのまま残して次の保存で送れるようにする。再取得を待つ間の入力も
          // 含めるよう、取得が終わってから判定する。入力されていない項目は、
          // サーバーが補った値（Slack連携の uuid など）を持つ取得結果に揃える。
          const editedKeys = markEditsSince(submittedFormModel)
          const current = formModelRef.current
          setFormModel(
            editedKeys.reduce<MeFormModel>(
              (merged, key) => ({ ...merged, [key]: current[key] }),
              refetchedFormModel
            )
          )
        } else {
          setFormModel(refetchedFormModel)
          refreshedPageData()
          refreshEditedFormData()
        }
        if (shouldRefreshUserRef.current) {
          shouldRefreshUserRef.current = false
          // 名前やアイコンはヘッダーやコメントなど全画面が Auth 保持のユーザー情報を
          // 参照するため、保存後に更新しないと旧い表示が残る。refreshUser は取得結果を
          // 検証せずに保持するため、接続できない状態で呼ぶとヘッダーの表示が消える。
          // 画面の表示には使わないため待たない。失敗はグローバルエラーハンドラで通知する。
          Auth.getCurrentTenant()?.refreshUser()
        }
        return detail
      } finally {
        setIsLoading(false)
      }
    },
    [
      setFormModel,
      formModelRef,
      refreshedPageData,
      refreshEditedFormData,
      markEditsSince,
    ]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const reload = useCallback(async () => {
    await fetchData()
  }, [fetchData])

  const submit = useCallback(async () => {
    if (isSubmittingRef.current) return
    if (!entity) return
    const currentFormModel = formModelRef.current
    isSubmittingRef.current = true
    setIsSubmitting(true)
    setIsLoading(true)
    try {
      const response = await updateMeDelta(
        toDeltaInput(entity, currentFormModel)
      )
      if (!response) return
      // 更新がサーバー側で拒否されると 200 + ワーニングメッセージが返る
      // （更新は反映されない）ため、成功メッセージは出さずに警告を表示する。
      if (response.hasWarning) {
        const messages = extractValuesFromResponse(response.json, 'messages')
        handleWarning(messages, _ => ({
          code: currentFormModel.code,
          name: currentFormModel.name,
        }))
        return
      }
      if (response.hasError) {
        dispatch(
          addScreenMessage(APPLICATION_FUNCTION_EXTERNAL_ID.ME_EDIT, {
            type: MessageLevel.ERROR,
            title: intl.formatMessage({ id: 'meV2.update.failed' }),
          })
        )
        return
      }
      dispatch(
        addScreenMessage(APPLICATION_FUNCTION_EXTERNAL_ID.ME_EDIT, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'update.completed.title' }),
        })
      )
      shouldRefreshUserRef.current = true
      // 再取得の失敗の通知は、接続失敗ならfetchラッパー、それ以外はグローバル
      // エラーハンドラに任せる。
      let refetched: UserDetailDto | undefined
      try {
        refetched = await fetchData(currentFormModel)
      } finally {
        if (!refetched) {
          // 差分の基準が保存前の値のまま残り、次の保存で保存済みの変更を戻しても
          // 差分に載らなくなるため、単票を隠してリロードで取り直させる。
          setEntity(undefined)
          // 更新自体は成功しているため、保存した項目の編集マーカーは消す（未保存の
          // 変更が残っているとユーザーに誤認させないため）。
          markEditsSince(currentFormModel)
        }
      }
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
      setIsLoading(false)
    }
  }, [entity, fetchData, dispatch, formModelRef, markEditsSince])

  return {
    isLoading,
    isSubmitting,
    edited,
    editedFormData,
    formModel,
    entity,
    onChangeMe,
    submit,
    reload,
  }
}
